import React from 'react';
import {
  Box,
  Flex,
  Text,
  Card,
  Button,
  Heading,
  Badge,
  Link
} from '@radix-ui/themes';
import SectionHeading from './common/SectionHeading';
import { CheckCircle, ExternalLink } from 'lucide-react';
import {
  ArrowTopRightIcon,
  CalendarIcon
} from '@radix-ui/react-icons';
import { formatFrequency, getFrequencyColor } from '../utils/frequencyUtils';

interface SurveyResult {
  name: string;
  frequency: string;
  url: string;
  link?: string;
}

interface SurveyResultsDisplayProps {
  surveys: SurveyResult[];
  onComplete: () => void;
}

const SurveyResultsDisplay: React.FC<SurveyResultsDisplayProps> = ({
  surveys,
  onComplete
}) => {
  // Sort surveys to ensure consistent ordering: daily first, then weekly, then others
  const parseSortKey = (s: any) => {
    const freq = (s.frequency || '').toLowerCase();
    const name = (s.name || '').toLowerCase();
    if (freq === 'unscheduled' || name.includes('unscheduled') || name.includes('on-demand')) return { type: 3, day: 0, hour: 0, minute: 0 };
    const parts = freq.split(' ');
    if (parts.length === 5) {
      const minute = parseInt(parts[0]) || 0;
      const hour = parseInt(parts[1]) || 0;
      if (parts[4] === '*') return { type: 0, day: 0, hour, minute };
      const day = parseInt(parts[4]) || 0;
      return { type: 1, day: day === 0 ? 7 : day, hour, minute };
    }
    const timeMatch = name.match(/(\d{1,2}):(\d{2})/);
    const fHour = timeMatch ? parseInt(timeMatch[1]) : 0;
    const fMin = timeMatch ? parseInt(timeMatch[2]) : 0;
    const dayNames: Record<string, number> = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 7 };
    const dayMatch = Object.entries(dayNames).find(([d]) => name.includes(d));
    if (name.includes('daily') || freq.includes('daily')) return { type: 0, day: 0, hour: fHour, minute: fMin };
    if (name.includes('weekly') || freq.includes('weekly')) return { type: 1, day: dayMatch ? dayMatch[1] : 0, hour: fHour, minute: fMin };
    return { type: 2, day: 0, hour: 0, minute: 0 };
  };

  const getSortKey = (s: any) => parseSortKey(s).type;

  const sortedSurveys = [...surveys].sort((a, b) => {
    const ka = parseSortKey(a);
    const kb = parseSortKey(b);
    if (ka.type !== kb.type) return ka.type - kb.type;
    if (ka.day !== kb.day) return ka.day - kb.day;
    if (ka.hour !== kb.hour) return ka.hour - kb.hour;
    return ka.minute - kb.minute;
  });

  const dailySurveys = sortedSurveys.filter(s => getSortKey(s) === 0);
  const weeklySurveys = sortedSurveys.filter(s => getSortKey(s) === 1);
  const otherSurveys = sortedSurveys.filter(s => getSortKey(s) === 2);
  const unscheduledSurveys = sortedSurveys.filter(s => getSortKey(s) === 3);

  return (
    <Card className="p-6">
      <Heading size="5" className="text-slate-800 mb-4">
        Generated Questions & Voice Scripts
      </Heading>
      
      <Text size="3" className="text-slate-600 mb-8 block">
        Your care plan has been approved and survey questions have been generated successfully.
      </Text>

      {/* Survey Questions Section */}
      <SectionHeading>
        Survey Questions
      </SectionHeading>

      {/* Daily Surveys */}
      {dailySurveys.length > 0 && (
        <Box className="mb-6">
          {dailySurveys.map((survey, index) => (
            <Card key={index} className="p-4 mb-3">
              <Flex justify="between" align="center">
                <Box>
                  <Flex align="center" gap="2" className="mb-1">
                    <Heading size="3" className="text-slate-800">
                      {survey.name}
                    </Heading>
                    {(survey.link || survey.url) && (
                      <Link 
                        href={survey.link || survey.url} 
                        target="_blank"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <ArrowTopRightIcon width="16" height="16" />
                      </Link>
                    )}
                  </Flex>
                  <Text size="2" className="text-slate-600">
                    Frequency: {survey.frequency}
                  </Text>
                </Box>
              </Flex>
            </Card>
          ))}
        </Box>
      )}

      {/* Weekly Surveys */}
      {weeklySurveys.length > 0 && (
        <Box className="mb-6">
          {weeklySurveys.map((survey, index) => (
            <Card key={index} className="p-4 mb-3">
              <Flex justify="between" align="center">
                <Box>
                  <Flex align="center" gap="2" className="mb-1">
                    <Heading size="3" className="text-slate-800">
                      {survey.name}
                    </Heading>
                    {(survey.link || survey.url) && (
                      <Link 
                        href={survey.link || survey.url} 
                        target="_blank"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <ArrowTopRightIcon width="16" height="16" />
                      </Link>
                    )}
                  </Flex>
                  <Text size="2" className="text-slate-600">
                    Frequency: {survey.frequency}
                  </Text>
                </Box>
              </Flex>
            </Card>
          ))}
        </Box>
      )}

      {/* Other Frequency Surveys */}
      {otherSurveys.length > 0 && (
        <Box className="mb-6">
          {otherSurveys.map((survey, index) => (
            <Card key={index} className="p-4 mb-3">
              <Flex justify="between" align="center">
                <Box>
                  <Flex align="center" gap="2" className="mb-1">
                    <Heading size="3" className="text-slate-800">
                      {survey.name}
                    </Heading>
                    {(survey.link || survey.url) && (
                      <Link 
                        href={survey.link || survey.url} 
                        target="_blank"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <ArrowTopRightIcon width="16" height="16" />
                      </Link>
                    )}
                  </Flex>
                  <Text size="2" className="text-slate-600">
                    Frequency: {survey.frequency}
                  </Text>
                </Box>
              </Flex>
            </Card>
          ))}
        </Box>
      )}

      {unscheduledSurveys.length > 0 && (
        <Box className="mb-6">
          {unscheduledSurveys.map((survey, index) => (
            <Card key={index} className="p-4 mb-3" style={{ borderColor: 'var(--gray-5)' }}>
              <Flex justify="between" align="center">
                <Box>
                  <Flex align="center" gap="2" className="mb-1">
                    <Heading size="3" className="text-slate-800">
                      {survey.name}
                    </Heading>
                    {(survey.link || survey.url) && (
                      <Link
                        href={survey.link || survey.url}
                        target="_blank"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <ArrowTopRightIcon width="16" height="16" />
                      </Link>
                    )}
                  </Flex>
                  <Text size="2" color="gray">
                    On-Demand — send manually
                  </Text>
                </Box>
              </Flex>
            </Card>
          ))}
        </Box>
      )}

      {/* Summary */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-semibold text-green-800">Care Plan Successfully Generated</h3>
              </div>
              <p className="text-green-700">
                {surveys.length} surveys have been generated.
              </p>
            </div>

      {/* Completion Message */}
      <Flex direction="column" align="center" gap="3" className="mt-4">
        <Heading size="5" className="text-green-700">
          Onboarding Complete!
        </Heading>
        <Text className="text-slate-600">
          Patient onboarding has been completed successfully.
        </Text>
        <Button
          size="3"
          onClick={() => onComplete()}
        >
          Done
        </Button>
      </Flex>
    </Card>
  );
};

export default SurveyResultsDisplay;